import { useEffect, useRef } from 'react';

// The worker's entire job is to be a metronome that the browser won't
// throttle: postMessage('tick') on a fixed cadence. Inlined via Blob URL so
// there's no separate asset to serve or cache-bust.
const WORKER_SOURCE = `
  let intervalId = null;
  onmessage = (event) => {
    if (event.data && event.data.type === 'start') {
      clearInterval(intervalId);
      intervalId = setInterval(() => postMessage('tick'), event.data.intervalMs);
    }
  };
`;

/**
 * Runs `callback` immediately and then on a fixed `intervalMs` cadence,
 * REGARDLESS of tab visibility. Deliberately the opposite trade-off from
 * useVisibilityInterval: the dashboard heartbeat is a presence signal the
 * server reads while the agent works in BrightPattern in another window, and
 * browsers throttle background-tab `setInterval` far past the server's
 * heartbeat grace — a backgrounded (not closed) dashboard was reading as an
 * abandoned one. Web Worker timers are exempt from background-tab throttling,
 * so the cadence holds while the tab merely isn't focused; an actually-closed
 * tab terminates the worker, which is exactly the signal the server should
 * see. Falls back to a plain interval where Workers are unavailable (tests).
 *
 * Same latest-callback ref contract as useVisibilityInterval: an inline
 * closure per render does not tear down and restart the interval.
 */
export const useWorkerInterval = (
  callback: () => void,
  intervalMs: number,
  enabled = true,
): void => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    callbackRef.current();

    if (typeof Worker === 'undefined') {
      const intervalId = window.setInterval(() => callbackRef.current(), intervalMs);

      return () => window.clearInterval(intervalId);
    }

    const blobUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    const worker = new Worker(blobUrl);

    worker.onmessage = () => callbackRef.current();
    worker.postMessage({ type: 'start', intervalMs });

    return () => {
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
    };
  }, [enabled, intervalMs]);
};
